import { HARVEST_REACH_SIXTEENTHS, SIXTEENTHS, type Rules, type ToolKind } from "odyssey-shared";
import { chunkKey } from "../chunk-key.ts";
import type { InteractTarget } from "../interact-target.ts";
import type { HarvestMarks, PageMark } from "./marks.ts";

/**
 * What E harvests and the prompt names (ADR-142 decision 3 and 3a): the nearest harvestable prop in reach that is not
 * gone, while a tool of the kind it needs is in hand. With no such tool the lookup offers nothing, so no prompt shows on
 * the grass; the map server asks for the same tool and refuses `tool` without it. The answer is one target object
 * the lookup keeps and writes in place, in the shape the interaction prompt draws any target in: the verb `harvest`, a
 * handle no entity has, and the box of the prop's art.
 */

/**
 * The verb the prompt names a harvest by; the prompt's label is this word in sentence case.
 */
export const HARVEST_VERB = "harvest";

/**
 * The handle a harvest target carries: no entity has it.
 */
export const HARVEST_HANDLE = -2;

/**
 * Whether a tool of a kind is in hand: the tool belt's selected slot holds an item that is such a tool and the bag holds
 * at least one of it. The action bar holds no tool (ADR-142 decision 3b).
 *
 * @param rules The page's rules.
 * @param selected The item on the belt's selected slot, or null for none selected.
 * @param bag The page's bag slots.
 * @param kind The tool kind.
 * @returns True when one is.
 */
export function holdsTool(rules: Rules, selected: string | null, bag: readonly { readonly item: string | null; readonly quantity: number }[], kind: ToolKind): boolean {
  return selected !== null && rules.items.get(selected)?.tool === kind && bag.some((held) => held.item === selected && held.quantity > 0);
}

/**
 * What the lookup reads of the page.
 */
export interface HarvestLookSource {
  readonly marks: () => HarvestMarks | null;
  readonly hidden: (n: number) => boolean;
  readonly holds: (tool: ToolKind) => boolean;
  readonly busy?: () => boolean;
  readonly chunkSize: () => number;
}

/**
 * The lookup.
 */
export class HarvestLook {
  /**
   * The index of the prop the target names, -1 for none.
   */
  public index = -1;
  private readonly found: { -readonly [K in keyof InteractTarget]: InteractTarget[K] } = { found: false, waiting: false, handle: -1, verb: "", left: 0, top: 0, right: 0, bottom: 0 };

  /**
   * @param source What it reads of the page.
   */
  public constructor(private readonly source: HarvestLookSource) {}

  /**
   * The prop E would harvest for a character standing at a point, written into the object returned every call.
   *
   * @param x The character's feet across, in sixteenths.
   * @param y Its feet down.
   * @returns The target, `found` false when no prop is in reach, no tool is in hand or a hold already runs.
   */
  public current(x: number, y: number): InteractTarget {
    const marks = this.source.marks();
    this.index = -1;
    const target = this.found;
    target.found = false;
    target.handle = -1;
    if (marks === null || this.source.busy?.() === true) {
      return target;
    }
    const chunk = this.source.chunkSize() * SIXTEENTHS;
    const column = Math.floor(x / chunk);
    const row = Math.floor(y / chunk);
    let best: PageMark | null = null;
    let nearest = HARVEST_REACH_SIXTEENTHS * HARVEST_REACH_SIXTEENTHS + 1;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const list = marks.byChunk.get(chunkKey(column + dx, row + dy));
        if (list === undefined) {
          continue;
        }
        for (const mark of list) {
          const across = mark.x - x;
          const down = mark.y - y;
          const distance = across * across + down * down;
          if ((distance < nearest || (best !== null && distance === nearest && mark.n < best.n)) && !this.source.hidden(mark.n)) {
            best = mark;
            nearest = distance;
          }
        }
      }
    }
    if (best === null || !this.source.holds(best.tool)) {
      return target;
    }
    this.index = best.n;
    target.found = true;
    target.handle = HARVEST_HANDLE;
    target.verb = HARVEST_VERB;
    target.left = best.left;
    target.top = best.top;
    target.right = best.right;
    target.bottom = best.bottom;
    return target;
  }
}
